// Embeddable profile administration table.
// Accepts a `profile` prop matching the backend Profile struct.
// Calls `onSave(updatedProfile)` when roles / functional_areas are saved.

import React, { useState, useEffect } from 'react';
import { TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

const AVAILABLE_ROLES = ['Sales', 'Production', 'Admin'];
const AVAILABLE_FUNCTIONAL_AREAS = ['Dashboard', 'Billboard', 'Orders', 'Invoices', 'Customers', 'Spots', 'Logs', 'Packages', 'Manual Upload', 'Admin', 'Profile'];

function TagBadge({ label, onRemove }) {
    return (
        <span className="inline-flex items-center gap-x-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
            {label}
            {onRemove && (
                <button
                    type="button"
                    onClick={() => onRemove(label)}
                    className="ml-1 rounded-sm text-indigo-400 hover:text-indigo-600 focus:outline-none"
                >
                    <TrashIcon className="h-3 w-3" aria-hidden="true" />
                    <span className="sr-only">Remove {label}</span>
                </button>
            )}
        </span>
    );
}

function TagEditor({ label, items, availableOptions, onChange }) {
    const [inputValue, setInputValue] = useState('');

    const addFromSelect = (value) => {
        if (value && !items.includes(value)) {
            onChange([...items, value]);
        }
        setInputValue('');
    };

    const addCustom = () => {
        const trimmed = inputValue.trim();
        if (trimmed && !items.includes(trimmed)) {
            onChange([...items, trimmed]);
        }
        setInputValue('');
    };

    const remove = (tag) => {
        onChange(items.filter((t) => t !== tag));
    };

    const unselected = availableOptions.filter((o) => !items.includes(o));

    return (
        <div>
            <div className="flex flex-wrap gap-1 mb-2">
                {items.length === 0 && (
                    <span className="text-sm text-gray-400 italic">None assigned</span>
                )}
                {items.map((tag) => (
                    <TagBadge key={tag} label={tag} onRemove={remove} />
                ))}
            </div>
            <div className="flex items-center gap-2 mt-1">
                {unselected.length > 0 && (
                    <select
                        value=""
                        onChange={(e) => addFromSelect(e.target.value)}
                        className="block rounded-md border-0 py-1 pl-2 pr-8 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs sm:leading-6"
                    >
                        <option value="" disabled>+ Add {label}</option>
                        {unselected.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                )}
                <div className="flex items-center gap-1">
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustom())}
                        placeholder={`Custom ${label}…`}
                        className="block rounded-md border-0 py-1 px-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-xs sm:leading-6"
                    />
                    <button
                        type="button"
                        onClick={addCustom}
                        disabled={!inputValue.trim()}
                        className="rounded-md bg-indigo-600 p-1 text-white shadow-sm hover:bg-indigo-500 disabled:opacity-40"
                    >
                        <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function AdminProfileTable({ profile, onSave }) {
    const [roles, setRoles] = useState([]);
    const [functionalAreas, setFunctionalAreas] = useState([]);
    const [salesQuota, setSalesQuota] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    useEffect(() => {
        if (profile) {
            setRoles(profile.roles ?? []);
            setFunctionalAreas(profile.functional_areas ?? []);
            setSalesQuota(profile.sales_quota ?? '');
            setIsDirty(false);
        }
    }, [profile]);

    const handleRolesChange = (updated) => {
        setRoles(updated);
        setIsDirty(true);
        setSaveSuccess(false);
    };

    const handleFunctionalAreasChange = (updated) => {
        setFunctionalAreas(updated);
        setIsDirty(true);
        setSaveSuccess(false);
    };

    const handleSalesQuotaChange = (value) => {
        setSalesQuota(value);
        setIsDirty(true);
        setSaveSuccess(false);
    };

    const handleSave = async () => {
        if (!onSave) return;
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);
        try {
            await onSave({ ...profile, sales_quota: salesQuota, roles, functional_areas: functionalAreas });
            setIsDirty(false);
            setSaveSuccess(true);
        } catch (err) {
            setSaveError(err.message ?? 'Save failed');
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        setRoles(profile?.roles ?? []);
        setFunctionalAreas(profile?.functional_areas ?? []);
        setSalesQuota(profile?.sales_quota ?? '');
        setIsDirty(false);
        setSaveSuccess(false);
        setSaveError(null);
    };

    if (!profile) {
        return (
            <div className="text-center py-8 text-gray-400 text-sm italic">
                No profile selected.
            </div>
        );
    }

    const formatDate = (val) => {
        if (!val) return '—';
        try {
            return new Date(val).toLocaleString();
        } catch {
            return val;
        }
    };

    const rows = [
        { label: 'ID',            value: profile.id ?? '—' },
        { label: 'Username',      value: profile.username ?? '—' },
        { label: 'Email',         value: profile.email ?? '—' },
        { label: 'Provider',      value: profile.provider ?? '—' },
        { label: 'First Name',    value: profile.profileinfo?.first_name ?? '—' },
        { label: 'Last Name',     value: profile.profileinfo?.last_name ?? '—' },
        { label: 'Display Name',  value: profile.profileinfo?.display_name ?? '—' },
        { label: 'Avatar',        value: profile.profileinfo?.avatar_url
            ? <img src={profile.profileinfo.avatar_url} alt="avatar" className="h-8 w-8 rounded-full" />
            : '—'
        },
        { label: 'Created',       value: formatDate(profile.create_date) },
        { label: 'Modified',      value: formatDate(profile.modified_date) },
    ];

    return (
        <div className="flow-root">
            {/* Read-only profile info */}
            <div className="overflow-hidden rounded-lg ring-1 ring-gray-200 mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="py-2 pl-4 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 sm:pl-6 w-40">
                                Field
                            </th>
                            <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Value
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {rows.map((row) => (
                            <tr key={row.label} className="hover:bg-gray-50">
                                <td className="whitespace-nowrap py-2 pl-4 pr-3 text-xs font-medium text-gray-500 sm:pl-6">
                                    {row.label}
                                </td>
                                <td className="px-3 py-2 text-sm text-gray-900">
                                    {row.value}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Editable: Sales Quota */}
            <div className="rounded-lg ring-1 ring-gray-200 bg-white mb-4">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-900">Sales Quota</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Set this user's sales quota.</p>
                </div>
                <div className="px-4 py-3 sm:max-w-xs">
                    <input
                        id="sales_quota"
                        type="number"
                        step="0.01"
                        value={salesQuota}
                        onChange={(e) => handleSalesQuotaChange(e.target.value)}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        placeholder="0"
                    />
                </div>
            </div>

            {/* Editable: Roles */}
            <div className="rounded-lg ring-1 ring-gray-200 bg-white mb-4">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-900">Roles</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Control what this user can access.</p>
                </div>
                <div className="px-4 py-3">
                    <TagEditor
                        label="role"
                        items={roles}
                        availableOptions={AVAILABLE_ROLES}
                        onChange={handleRolesChange}
                    />
                </div>
            </div>

            {/* Editable: Functional Areas */}
            <div className="rounded-lg ring-1 ring-gray-200 bg-white mb-6">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                    <h3 className="text-sm font-semibold text-gray-900">Functional Areas</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Departments or areas this user belongs to.</p>
                </div>
                <div className="px-4 py-3">
                    <TagEditor
                        label="area"
                        items={functionalAreas}
                        availableOptions={AVAILABLE_FUNCTIONAL_AREAS}
                        onChange={handleFunctionalAreasChange}
                    />
                </div>
            </div>

            {/* Save / Reset bar */}
            {onSave && (
                <div className="flex items-center justify-between gap-x-4">
                    <div className="text-sm">
                        {saveSuccess && (
                            <span className="text-green-600 font-medium">Saved successfully.</span>
                        )}
                        {saveError && (
                            <span className="text-red-600 font-medium">{saveError}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-x-3">
                        <button
                            type="button"
                            onClick={handleReset}
                            disabled={!isDirty || isSaving}
                            className="text-sm font-semibold leading-6 text-gray-700 hover:text-gray-900 disabled:opacity-40"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!isDirty || isSaving}
                            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-40"
                        >
                            {isSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminProfileTable;
